import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "companyInput", "companyHidden", "companyResults",
    "addMembersBtn", "popover", "popoverSearch", "popoverList",
    "inviteModal", "inviteModalCompany", "inviteEmailInput", "inviteEmailError", "inviteSubmitBtn",
    "memberChips", "chip",
    "permissionsPopover",
    "card"
  ]

  static values = {
    membersUrl: String,
    validateEmailUrl: String,
    steps: { type: Array, default: [] },
    initialCompanyId: String,
    initialCompanyName: String,
    initialMembers: { type: Array, default: [] }
  }

  connect() {
    this._members = []
    this._selected = new Set()
    this._memberData = new Map() // email -> { permissions: number[], isExternal: boolean }
    this._isPrePopulating = false

    this._onClickOutside = (e) => {
      if (this.hasPopoverTarget && !this.popoverTarget.contains(e.target) &&
          (!this.hasAddMembersBtnTarget || !this.addMembersBtnTarget.contains(e.target))) {
        this._closePopover()
      }
      if (this._permissionsOpen && this.hasPermissionsPopoverTarget &&
          !this.permissionsPopoverTarget.contains(e.target) &&
          !e.target.closest("[data-action*='editPermissions']")) {
        this._closePermissionsPopover()
      }
    }
    document.addEventListener("click", this._onClickOutside)
    this._permissionsOpen = false

    if (this.initialCompanyIdValue && this.initialCompanyNameValue) {
      this._prePopulate()
    }
  }

  _prePopulate() {
    this._isPrePopulating = true
    if (this.hasCompanyInputTarget) this.companyInputTarget.value = this.initialCompanyNameValue
    if (this.hasCompanyHiddenTarget) this.companyHiddenTarget.value = this.initialCompanyIdValue
    this._companyName = this.initialCompanyNameValue

    if (this.hasAddMembersBtnTarget) {
      this.addMembersBtnTarget.classList.remove("hidden")
      this.addMembersBtnTarget.classList.add("inline-flex")
    }

    this.initialMembersValue.forEach(m => {
      const email = m.email
      const name = m.name || email
      const permissions = m.permissions || this._defaultPermissions()
      const isExternal = m.is_external || false
      this._selected.add(email)
      this._memberData.set(email, { permissions, isExternal })
      this._addChip(name, email)
    })

    this._loadMembers(this.initialCompanyIdValue)
    this._isPrePopulating = false
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    if (this._onCompanyCreated) {
      document.removeEventListener("new-company:created", this._onCompanyCreated)
    }
  }

  // -- Company Selected (from autocomplete) --

  companySelected(event) {
    const { id, name } = event.detail
    this._companyName = name
    this._loadMembers(id)
  }

  registerCompany(event) {
    const modal = document.querySelector('[data-modal-id-value="new-company"]')
    if (modal) {
      modal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
      // Start the Name with what was typed in the search, so it isn't typed twice.
      const typed = (event?.detail?.query ?? (this.hasCompanyInputTarget ? this.companyInputTarget.value : "")).trim()
      const nameField = modal.querySelector('[data-new-company-target="name"]')
      if (nameField && typed) nameField.value = typed
    }

    if (this._onCompanyCreated) {
      document.removeEventListener("new-company:created", this._onCompanyCreated)
    }

    this._onCompanyCreated = (event) => {
      const { id, name } = event.detail
      if (this.hasCompanyInputTarget) this.companyInputTarget.value = name
      if (this.hasCompanyHiddenTarget) this.companyHiddenTarget.value = id
      this._loadMembers(id)
      document.removeEventListener("new-company:created", this._onCompanyCreated)
    }
    document.addEventListener("new-company:created", this._onCompanyCreated)
  }

  async _loadMembers(companyId) {
    if (!this.membersUrlValue) return

    try {
      const url = `${this.membersUrlValue}?id=${companyId}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      if (!Array.isArray(data)) return
      this._members = data

      if (this.hasAddMembersBtnTarget) {
        this.addMembersBtnTarget.classList.remove("hidden")
        this.addMembersBtnTarget.classList.add("inline-flex")
      }
    } catch { /* ignore */ }
  }

  // -- Member Popover --

  togglePopover() {
    if (!this.hasPopoverTarget) return
    this._closePermissionsPopover()

    if (this.popoverTarget.classList.contains("hidden")) {
      this._openPopover()
    } else {
      this._closePopover()
    }
  }

  _openPopover() {
    this._renderPopoverList("")
    this.popoverTarget.classList.remove("hidden")

    if (this.hasAddMembersBtnTarget) {
      const rect = this.addMembersBtnTarget.getBoundingClientRect()
      const popover = this.popoverTarget
      popover.style.top = `${rect.bottom + 4}px`
      popover.style.right = `${window.innerWidth - rect.right}px`
      popover.style.left = ""

      requestAnimationFrame(() => {
        const pRect = popover.getBoundingClientRect()
        if (pRect.bottom > window.innerHeight - 8) {
          popover.style.top = ""
          popover.style.bottom = `${window.innerHeight - rect.top + 4}px`
        }
      })
    }

    if (this.hasPopoverSearchTarget) {
      this.popoverSearchTarget.value = ""
      this.popoverSearchTarget.focus()
    }
  }

  _closePopover() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add("hidden")
      this.popoverTarget.style.bottom = ""
    }
  }

  filterMembers() {
    const query = this.popoverSearchTarget.value.trim().toLowerCase()
    this._renderPopoverList(query)
  }

  _renderPopoverList(query) {
    if (!this.hasPopoverListTarget) return

    const filtered = this._members.filter(m => {
      if (!query) return true
      return `${m.name} ${m.email}`.toLowerCase().includes(query)
    })

    let html = ""

    if (filtered.length === 0 && !query) {
      html = `<div class="px-3 py-4 text-xs text-gray-400 text-center">No members in this company</div>`
    } else if (filtered.length === 0) {
      html = `<div class="px-3 py-4 text-xs text-gray-400 text-center">No matching members</div>`
    } else {
      html = filtered.map(m => {
        const checked = this._selected.has(m.email)
        return `<label class="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors">
          <input type="checkbox" ${checked ? "checked" : ""}
                 value="${this._esc(m.email)}"
                 data-name="${this._esc(m.name)}"
                 data-action="change->collaborator-card#toggleMember"
                 class="form-checkbox cursor-pointer">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900">${this._esc(m.name)}</div>
            <div class="text-xs text-text-muted">${this._esc(m.email)}</div>
          </div>
        </label>`
      }).join("")
    }

    this.popoverListTarget.innerHTML = html
  }

  // -- Member toggle --

  toggleMember(event) {
    const checkbox = event.currentTarget
    const email = checkbox.value
    const name = checkbox.dataset.name

    if (checkbox.checked) {
      this._selected.add(email)
      this._memberData.set(email, { permissions: this._defaultPermissions(), isExternal: false })
      this._addChip(name, email)
      // Auto-open permissions popover for newly added member
      requestAnimationFrame(() => {
        const chip = this.element.querySelector(`[data-chip-email="${CSS.escape(email)}"]`)
        if (chip) this._openPermissionsPopover(email, chip)
      })
    } else {
      this._selected.delete(email)
      this._memberData.delete(email)
      this._removeChipByEmail(email)
    }
  }

  // -- Invite Modal --

  showInviteModal() {
    this._closePopover()
    if (!this.hasInviteModalTarget) return

    if (this.hasInviteModalCompanyTarget) {
      this.inviteModalCompanyTarget.textContent = this._companyName || ""
    }

    if (this.hasInviteEmailInputTarget) this.inviteEmailInputTarget.value = ""
    if (this.hasInviteEmailErrorTarget) {
      this.inviteEmailErrorTarget.classList.add("hidden")
      this.inviteEmailErrorTarget.textContent = ""
    }
    if (this.hasInviteSubmitBtnTarget) {
      this.inviteSubmitBtnTarget.disabled = false
      this.inviteSubmitBtnTarget.textContent = "Add"
    }

    this.inviteModalTarget.classList.remove("hidden")
    setTimeout(() => this.inviteEmailInputTarget?.focus(), 50)
  }

  closeInviteModal() {
    if (this.hasInviteModalTarget) {
      this.inviteModalTarget.classList.add("hidden")
    }
  }

  inviteEmailKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitInviteEmail()
    } else if (event.key === "Escape") {
      this.closeInviteModal()
    }
  }

  async submitInviteEmail() {
    if (!this.hasInviteEmailInputTarget) return

    const email = this.inviteEmailInputTarget.value.trim().toLowerCase()

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._showInviteError("Please enter a valid email address.")
      return
    }

    if (this._selected.has(email)) {
      this._showInviteError("This member has already been added.")
      return
    }

    if (this.validateEmailUrlValue) {
      this._setInviteLoading(true)
      try {
        const url = `${this.validateEmailUrlValue}?email=${encodeURIComponent(email)}`
        const resp = await fetch(url, { headers: { "Accept": "application/json" } })
        if (resp.ok) {
          const data = await resp.json()
          if (!data.is_valid) {
            this._setInviteLoading(false)
            this._showInviteError("This email address doesn't exist. Please double-check for typos and try again.")
            return
          }
        }
      } catch { /* proceed if validation endpoint fails */ }
      this._setInviteLoading(false)
    }

    this._selected.add(email)
    this._memberData.set(email, { permissions: this._defaultPermissions(), isExternal: false })
    this._addChip(email, email)
    this.closeInviteModal()

    // Auto-open permissions popover for invited member
    requestAnimationFrame(() => {
      const chip = this.element.querySelector(`[data-chip-email="${CSS.escape(email)}"]`)
      if (chip) this._openPermissionsPopover(email, chip)
    })
  }

  _showInviteError(msg) {
    if (!this.hasInviteEmailErrorTarget) return
    this.inviteEmailErrorTarget.textContent = msg
    this.inviteEmailErrorTarget.classList.remove("hidden")
    this.inviteEmailInputTarget?.classList.add("border-red-400", "ring-1", "ring-red-400")
  }

  _setInviteLoading(loading) {
    if (!this.hasInviteSubmitBtnTarget) return
    this.inviteSubmitBtnTarget.disabled = loading
    this.inviteSubmitBtnTarget.textContent = loading ? "Validating..." : "Add"
  }

  // -- Chips --

  _addChip(label, email) {
    if (!this.hasMemberChipsTarget) return
    if (this.element.querySelector(`[data-chip-email="${CSS.escape(email)}"]`)) return

    const data = this._memberData.get(email) || { permissions: this._defaultPermissions(), isExternal: false }
    const chip = document.createElement("span")
    chip.dataset.collaboratorCardTarget = "chip"
    chip.dataset.chipEmail = email
    chip.dataset.chipPermissions = JSON.stringify(data.permissions)
    chip.dataset.chipExternal = data.isExternal ? "true" : "false"
    chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
    chip.style.cssText = "font-size:14px;color:#6B7280;background:#6B728012;border:1px solid #6B72804D;"

    this._renderChipContent(chip, label, email, data)
    this.memberChipsTarget.appendChild(chip)
  }

  _renderChipContent(chip, label, email, data) {
    const permsText = data.permissions.length > 0 ? `[${data.permissions.join(",")}]` : ""
    const extBadge = data.isExternal
      ? `<span class="px-1 py-px text-[10px] font-bold bg-amber-100 text-amber-700 rounded">EXT</span>`
      : ""

    chip.innerHTML = `
      ${this._esc(label)}
      <span class="text-xs text-gray-400">${permsText}</span>
      ${extBadge}
      <button type="button" data-action="collaborator-card#editPermissions" data-email="${this._esc(email)}"
              class="ml-0.5 text-gray-400 hover:text-brand cursor-pointer" title="Edit permissions">
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
        </svg>
      </button>
      <button type="button" data-action="collaborator-card#removeChip" data-email="${this._esc(email)}"
              class="ml-0.5 hover:text-red-500 cursor-pointer">&times;</button>
    `
  }

  _updateChipDisplay(email) {
    const chip = this.element.querySelector(`[data-chip-email="${CSS.escape(email)}"]`)
    if (!chip) return

    const data = this._memberData.get(email)
    if (!data) return

    chip.dataset.chipPermissions = JSON.stringify(data.permissions)
    chip.dataset.chipExternal = data.isExternal ? "true" : "false"

    // Preserve the label (first text node)
    const label = chip.childNodes[0]?.textContent?.trim() ||
                  chip.querySelector("[data-chip-email]")?.textContent?.trim() || email

    // Find the original label from chip text (first meaningful text before the brackets)
    const allText = chip.textContent
    const nameMatch = allText.split("[")[0]?.trim().split("\n").map(s => s.trim()).filter(Boolean)[0]
    const displayLabel = nameMatch || email

    this._renderChipContent(chip, displayLabel, email, data)
  }

  removeChip(event) {
    const email = event.currentTarget.dataset.email
    this._selected.delete(email)
    this._memberData.delete(email)
    this._removeChipByEmail(email)

    if (this.hasPopoverTarget && !this.popoverTarget.classList.contains("hidden")) {
      const cb = this.popoverTarget.querySelector(`input[type="checkbox"][value="${CSS.escape(email)}"]`)
      if (cb) cb.checked = false
    }
  }

  // -- Permissions Popover --

  editPermissions(event) {
    event.stopPropagation()
    const email = event.currentTarget.dataset.email
    const chip = this.element.querySelector(`[data-chip-email="${CSS.escape(email)}"]`)
    if (chip) {
      this._closePopover()
      this._openPermissionsPopover(email, chip)
    }
  }

  _openPermissionsPopover(email, anchorEl) {
    this._closePermissionsPopover()
    if (!this.hasPermissionsPopoverTarget) return

    this._permissionsEmail = email
    this._permissionsOpen = true
    const data = this._memberData.get(email) || { permissions: this._defaultPermissions(), isExternal: false }

    const steps = this.stepsValue
    let checkboxesHtml = steps.map(step => {
      const checked = data.permissions.includes(step.number) ? "checked" : ""
      return `<label class="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 px-1 rounded">
        <input type="checkbox" ${checked} value="${step.number}"
               data-action="change->collaborator-card#toggleStepPermission"
               class="form-checkbox cursor-pointer">
        <span class="text-sm text-gray-700">Step ${step.number}: ${this._esc(step.name)}</span>
      </label>`
    }).join("")

    const externalChecked = data.isExternal ? "checked" : ""

    this.permissionsPopoverTarget.innerHTML = `
      <div class="p-3 space-y-2">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Step Permissions</p>
        <div class="space-y-0.5">${checkboxesHtml}</div>
        <div class="border-t border-gray-100 pt-2 mt-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" ${externalChecked}
                   data-action="change->collaborator-card#toggleExternal"
                   class="form-checkbox cursor-pointer">
            <span class="text-sm font-medium text-gray-700">Set As External User</span>
          </label>
        </div>
        <div class="pt-1">
          <button type="button" data-action="collaborator-card#donePermissions"
                  class="w-full px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-700 transition-colors cursor-pointer">
            Done
          </button>
        </div>
      </div>
    `

    const popover = this.permissionsPopoverTarget
    popover.classList.remove("hidden")

    // Position below the chip
    const rect = anchorEl.getBoundingClientRect()
    popover.style.position = "fixed"
    popover.style.top = `${rect.bottom + 4}px`
    popover.style.left = `${rect.left}px`
    popover.style.right = ""

    requestAnimationFrame(() => {
      const pRect = popover.getBoundingClientRect()
      // Flip above if overflows bottom
      if (pRect.bottom > window.innerHeight - 8) {
        popover.style.top = ""
        popover.style.bottom = `${window.innerHeight - rect.top + 4}px`
      }
      // Shift left if overflows right
      if (pRect.right > window.innerWidth - 8) {
        popover.style.left = ""
        popover.style.right = "8px"
      }
    })
  }

  _closePermissionsPopover() {
    if (this.hasPermissionsPopoverTarget) {
      this.permissionsPopoverTarget.classList.add("hidden")
    }
    this._permissionsOpen = false
    this._permissionsEmail = null
  }

  toggleStepPermission(event) {
    if (!this._permissionsEmail) return
    const stepNum = parseInt(event.currentTarget.value, 10)
    const data = this._memberData.get(this._permissionsEmail)
    if (!data) return

    if (event.currentTarget.checked) {
      if (!data.permissions.includes(stepNum)) {
        data.permissions.push(stepNum)
        data.permissions.sort((a, b) => a - b)
      }
    } else {
      data.permissions = data.permissions.filter(n => n !== stepNum)
    }
  }

  toggleExternal(event) {
    if (!this._permissionsEmail) return
    const data = this._memberData.get(this._permissionsEmail)
    if (!data) return
    data.isExternal = event.currentTarget.checked
  }

  donePermissions() {
    if (this._permissionsEmail) {
      this._updateChipDisplay(this._permissionsEmail)
    }
    this._closePermissionsPopover()
  }

  // -- Remove Card --

  removeCard() {
    this.element.remove()
  }

  // -- Utilities --

  _defaultPermissions() {
    return this.stepsValue.map(s => s.number)
  }

  _removeChipByEmail(email) {
    this.element.querySelectorAll(`span[data-chip-email="${CSS.escape(email)}"]`).forEach(el => el.remove())
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
